<template>
  <div class="settings">
    <PasswordChangeWrapper v-if="changePassword" @close="onChangePassword"/>
    <NicknameChangeWrapper v-if="changeNickname" :nick="userData.nick" @close="onChangeNickname" @onNewNickname="onNewNickname"/>
    
    <Nav>
      <template v-slot:logo-box>
        <Logo/>
      </template>
      <template v-slot:options-box>
        <DialoguesButton/>
        <AddButton/>
      </template>
      <template v-slot:account-box>
        <AccountButton/>
      </template>
    </Nav>

    <div class="body">
      <div class="content">

        <div class="loading-box" v-if="loading">
          <Loader/>
        </div>

        <div class="user-info-box" v-if="!loading">
          <div class="image-box">
            <div class="image-limiter">
              <Avatar :full="true"/>
            </div>
          </div>
          <div class="info-box">
            <div class="limiter">
              <div class="mail-box">
                <div class="label">email</div>
                <div class="value">{{ userData.email }}</div>
              </div>
              <div class="nick-box">
                <div class="label">nick</div>
                <div class="value">{{ userData.nick }}</div>
                <div class="edit" @click="onChangeNickname">
                  <font-awesome-icon :icon="['fas', 'pen']" class="icon" />
                </div>
              </div>
              <div class="buttons-box">
                <div class="change-password-button" @click="onChangePassword">Change Password</div>
              </div>
            </div>
          </div>
        </div>

        <div class="usage-box" v-if="!loadingStats">
          <div class="label">Usage</div>
          <div class="content-box">
            <div class="center-box">
              <div class="row">
                <div class="row-label">Plan</div>
                <div class="value">{{ userStats.plan }}</div>
              </div>
              <div class="row">
                <div class="row-label">Dialogues</div>
                <div class="value">{{ `${userStats.stats.dialogNumber}/${userStats.stats.dialogLimit}` }}</div>
              </div>
              <div class="row">
                <div class="row-label">Recodings</div>
                <div class="value">{{ `${userStats.stats.recordingsNumber}/${userStats.stats.recordingsLimit}` }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="upgrade-box" v-if="!loading && !loadingSubscriptionData">
          <div class="label">Choose your plan</div>
          <div class="plans-box">

            <div class="labels-box plan-box">
              <div class="title-box">Plan</div>
              <div class="price-box">Price / Monthly</div>
              <div class="dialogs-box">Dialogues Limit</div>
              <div class="recordings-box">Recodings Limit</div>
              <div class="recording-time-box">Recordings Storage</div>
              <div class="button-box"></div>
            </div>

            <div class="basic-box plan-box">
              <div class="title-box">Basic</div>
              <div class="price-box">
                <span class="name">Price / Monthly</span>
                <span class="value">Free</span>
              </div>
              <div class="dialogs-box">
                <span class="name">Dialogues Limit</span>
                <span class="value">5</span>
              </div>
              <div class="recordings-box">
                <span class="name">Recodings Limit</span>
                <span class="value">25</span>
              </div>
              <div class="recording-time-box">
                <span class="name">Recordings Storage</span>
                <span class="value">7 days</span>
              </div>
              <div class="button-box">
                <span v-if="userData.plan == 'basic'">current</span>
              </div>
            </div>

            <div class="silver-box plan-box">
              <div class="title-box">Silver</div>
              <div class="price-box">
                <span class="name">Price / Monthly</span>
                <span class="value">3 EUR</span>
              </div>
              <div class="dialogs-box">
                <span class="name">Dialogues Limit</span>
                <span class="value">25</span>
              </div>
              <div class="recordings-box">
                <span class="name">Recodings Limit</span>
                <span class="value">100</span>
              </div>
              <div class="recording-time-box">
                <span class="name">Recordings Storage</span>
                <span class="value">14 days</span>
              </div>
              <div class="button-box">
                <span v-if="userData.plan == 'silver'">current</span>
                <UpgradeButtonSilver v-if="userData.plan == 'basic'"/>
              </div>
            </div>

            <div class="gold-box plan-box">
              <div class="title-box">Gold</div>
              <div class="price-box">
                <span class="name">Price / Monthly</span>
                <span class="value">10 EUR</span>
              </div>
              <div class="dialogs-box">
                <span class="name">Dialogues Limit</span>
                <span class="value">50</span>
              </div>
              <div class="recordings-box">
                <span class="name">Recodings Limit</span>
                <span class="value">200</span>
              </div>
              <div class="recording-time-box">
                <span class="name">Recordings Storage</span>
                <span class="value">month</span>
              </div>
              <div class="button-box">
                <span v-if="userData.plan == 'gold'">current</span>
                <UpgradeButtonGold v-if="userData.plan != 'gold'"/>
              </div>
            </div>

          </div>
        </div>

        <div class="pro-box" v-if="!loading && !loadingSubscriptionData && userData.plan != 'basic'">
          <div class="label">Subscription Information</div>
          <div class="info">

            <div class="status-box">
              <div class="title">Status</div>
              <div class="value">
                <span :class="{active: subscriptionData.status == 'active'}">{{ subscriptionData.status }}</span>
              </div>
            </div>

            <div class="period-start">
              <div class="title">Period Start</div>
              <div class="value">
                <span>{{ getPeriodStart }}</span>
              </div>
            </div>

            <div class="period-end">
              <div class="title">Period End</div>
              <div class="value">
                <span>{{ getPeriodEnd }}</span>
              </div>
            </div>

            <div class="recharge-box">
              <div class="title">Auto Recharge</div>
              <div class="value">
                <span class="on" v-if="subscriptionType == 'stripe' && subscriptionData.status == 'active' && !subscriptionData.cancel_at_period_end">On</span>
                <span class="off" v-if="subscriptionData.status != 'active' || subscriptionData.cancel_at_period_end || subscriptionType == 'local'">Off</span>
              </div>
            </div>

          </div>
          <div class="button-bar">
            <CancelButton v-if="subscriptionType == 'stripe' && subscriptionData.status == 'active' && !subscriptionData.cancel_at_period_end" @reload="onReload"/>
            <RenewButton v-if="subscriptionType == 'stripe' && subscriptionData.status == 'active' && subscriptionData.cancel_at_period_end" @reload="onReload"/>
          </div>
        </div>

        <!-- Promo Codes -->
        <PromoCodesBox @reload="onReload" v-if="!loading"/>

      </div>
    </div>
  </div>
</template>

<script>
import alert from '@/lib/alert'
import axios from 'axios'
import CancelButton from '../components/Settings/CancelButton'
import RenewButton from '../components/Settings/RenewButton'
import UpgradeButtonSilver from '../components/Settings/UpgradeButtonSilver'
import UpgradeButtonGold from '../components/Settings/UpgradeButtonGold'
import PromoCodesBox from '../components/Settings/PromoCodesBox'
import PasswordChangeWrapper from '../components/Account/PasswordChangeWrapper'
import NicknameChangeWrapper from '../components/Account/NicknameChangeWrapper'
import Avatar from '@/components/Avatar'
import Loader from '@/components/Loader'
//Nav
import Nav from '@/components/Nav/Nav'
import Logo from '@/components/Nav/Logo'
import DialoguesButton from '@/components/Nav/DialoguesButton'
import AccountButton from '@/components/Nav/AccountButton'
import AddButton from '@/components/Nav/AddButton'
 
export default {
  name: 'Account',
  data() {
    return {
      userData: {},
      userStats: {},
      subscriptionData: {},
      subscriptionType: null,
      loading: true,
      loadingSubscriptionData: false,
      loadingStats: true,
      changePassword: false,
      changeNickname: false,
    }
  },
  components: {
    CancelButton,
    RenewButton,
    UpgradeButtonSilver,
    UpgradeButtonGold,
    PromoCodesBox,
    PasswordChangeWrapper,
    NicknameChangeWrapper,
    Avatar,
    Loader,
    //Nav
    Nav,
    Logo,
    DialoguesButton,
    AccountButton,
    AddButton
  },
  methods: {
    loadData() {
      axios({
        method: 'post',
        url: `${process.env.VUE_APP_SERVER_URL}loadUserData`,
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('token') 
        }
      }).then(result => {
        this.userData = result.data.user;
        this.subscriptionType = result.data.user.subscriptionType;
        this.loading = false;
        this.setAlert();
        if(result.data.user.plan != 'basic') {
          this.loadingSubscriptionData = true;
          this.loadSubscriptionData(result.data.user.subscriptionType);
        } 
      }).catch(err => {
        console.log(err);
        alert.showError(err);
        this.loading = false;
      })
    },
    loadSubscriptionData(subscriptionType) {
      if(subscriptionType == 'stripe') {
        axios({
          method: 'post',
          url: `${process.env.VUE_APP_SERVER_URL}getSubscriptionData`,
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token') 
          }
        }).then(result => {
          this.loadingSubscriptionData = false;
          this.subscriptionData = result.data.subscriptionData;
        }).catch(err => {
          console.log(err);
          alert.showError(err);
          this.loadingSubscriptionData = false;
        })
      }
      if(subscriptionType == 'local') {
        axios({
          method: 'post',
          url: `${process.env.VUE_APP_SERVER_URL}getLocalSubscriptionData`,
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token') 
          }
        }).then(result => {
          this.loadingSubscriptionData = false;
          this.subscriptionData = result.data.subscriptionData;
        }).catch(err => {
          console.log(err);
          alert.showError(err);
          this.loadingSubscriptionData = false;
        })
      }
    },
    loadUserStats() {
      this.loadingStats = true;
      axios({
        method: 'post',
        url: `${process.env.VUE_APP_SERVER_URL}loadUserStats`,
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('token') 
        },
      }).then(result => {
        this.userStats = result.data.stats;
        this.loadingStats = false;
      }).catch(err => {
        console.log(err);
        this.loadingStats = false;
      })
    },
    onReload() {
      this.loading = true;
      this.loadingSubscriptionData = true;
      this.loadData();
    },
    setAlert() {
      const urlParams = new URLSearchParams(window.location.search);
      const result = urlParams.get('result');
      if(result) {
        if(result == 'success' && this.userData.plan != 'basic') {
          console.log('successful upgrade');
          alert.showSuccess('Successful upgrade');
        }
        if(result == 'cancel' && this.userData.plan == 'basic') {
          console.log('unsuccessful upgrade');
          alert.showWarning('Unsuccessful upgrade');
        } 
      }
    },
    onChangePassword() {
      this.changePassword = !this.changePassword;
    },
    onChangeNickname() {
      this.changeNickname = !this.changeNickname;
    },
    onNewNickname(newNick) {
      this.userData.nick = newNick;
      this.changeNickname = !this.changeNickname;
    }
  },
  computed: {
    getPeriodStart() {
      if(this.subscriptionData) {

        if(this.subscriptionType == 'local') {
          let date = new Date(this.subscriptionData.start);
          let day = date.getDate();
          let month = date.getMonth();
          let year = date.getFullYear();
          let monthArray = new Array();
          monthArray[0] = "January";
          monthArray[1] = "February";
          monthArray[2] = "March";
          monthArray[3] = "April";
          monthArray[4] = "May";
          monthArray[5] = "June";
          monthArray[6] = "July";
          monthArray[7] = "August";
          monthArray[8] = "September";
          monthArray[9] = "October";
          monthArray[10] = "November";
          monthArray[11] = "December";
          return `${day} ${monthArray[month]} ${year}`;
        } else if(this.subscriptionType == 'stripe') {
          let date = new Date(this.subscriptionData.current_period_start * 1000);
          let day = date.getDate();
          let month = date.getMonth();
          let year = date.getFullYear();
          let monthArray = new Array();
          monthArray[0] = "January";
          monthArray[1] = "February";
          monthArray[2] = "March";
          monthArray[3] = "April";
          monthArray[4] = "May";
          monthArray[5] = "June";
          monthArray[6] = "July";
          monthArray[7] = "August";
          monthArray[8] = "September";
          monthArray[9] = "October";
          monthArray[10] = "November";
          monthArray[11] = "December";
          return `${day} ${monthArray[month]} ${year}`;
        } else {
          return '';
        }

      } else {
        return '';
      }
    },
    getPeriodEnd() {
      if(this.subscriptionData) {

        if(this.subscriptionType == 'local') {
          let date = new Date(this.subscriptionData.end);
          let day = date.getDate();
          let month = date.getMonth();
          let year = date.getFullYear();
          let monthArray = new Array();
          monthArray[0] = "January";
          monthArray[1] = "February";
          monthArray[2] = "March";
          monthArray[3] = "April";
          monthArray[4] = "May";
          monthArray[5] = "June";
          monthArray[6] = "July";
          monthArray[7] = "August";
          monthArray[8] = "September";
          monthArray[9] = "October";
          monthArray[10] = "November";
          monthArray[11] = "December";
          return `${day} ${monthArray[month]} ${year}`;
        } else if(this.subscriptionType == 'stripe') {
          let date = new Date(this.subscriptionData.current_period_end * 1000);
          let day = date.getDate();
          let month = date.getMonth();
          let year = date.getFullYear();
          let monthArray = new Array();
          monthArray[0] = "January";
          monthArray[1] = "February";
          monthArray[2] = "March";
          monthArray[3] = "April";
          monthArray[4] = "May";
          monthArray[5] = "June";
          monthArray[6] = "July";
          monthArray[7] = "August";
          monthArray[8] = "September";
          monthArray[9] = "October";
          monthArray[10] = "November";
          monthArray[11] = "December";
          return `${day} ${monthArray[month]} ${year}`;
        } else {
          return '';
        }

      } else {
        return '';
      }
    },
    getProfilePicture() {
      let color= null;
      if(this.userData.plan == 'basic') {
        color = '#11b6a5';
      }
      if(this.userData.plan == 'silver') {
        color = '#cccccc';
      }
      if(this.userData.plan == 'gold') {
        color = '#ebc659';
      }
      return {
        type: 'nick',
        nick: this.userData.nick[0].toUpperCase(),
        color
      }
    }
  },
  created() {
    this.loadData();
    this.loadUserStats();
  }
}
</script>

<style lang="scss" scoped>
.settings {
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

.body {
  width: 100%;
  min-height: calc(100vh - 5rem);
  background-color: white;
  display: flex;
  justify-content: center;
}

.content {
  width: 50rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 800px) {
    width: 90vw;
  }
}

.loading-box {
  width: 100%;
  height: 8rem;
  margin-top: 5rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.user-info-box {
  width: 100%;
  height: 12rem;
  margin-top: 5rem;
  display: flex;
  flex-direction: row;
  background-color: white;
  filter:drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.171));
  @media (max-width: 800px) {
    height: 10rem;
  }
  @media (max-width: 600px) {
    flex-direction: column;
    height: 20rem;
  }

  .image-box {
    width: 12rem;
    height: 12rem;
    display: flex;
    align-items: center;
    justify-content: center;
    @media (max-width: 800px) {
      height: 10rem;
      width: 10rem;
    }
    @media (max-width: 600px) {
      height: 8rem;
      width: 100%;
    }

    .image-limiter {
      width: 9rem;
      height: 9rem;
      display: flex;
      font-size: 5.5rem;

      @media (max-width: 800px) {
        height: 7rem;
        width: 7rem;
        font-size: 4.5rem;
      }
      @media (max-width: 600px) {
        height: 6rem;
        width: 6rem;
        font-size: 4.1rem;
      }
    }
  }

  .info-box {
    flex: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .limiter {
      width: 90%;
      height: 9rem;
      display: flex;
      flex-direction: column;
      @media (max-width: 800px) {
        height: 8rem;
      }
      @media (max-width: 600px) {
        height: 10rem;
        width: 80%;
      }

      .mail-box {
        width: 100%;
        flex: 1;
        display: flex;
        align-items: center;
      }

      .nick-box {
        width: 100%;
        flex: 1;
        display: flex;
        align-items: center;

        .nickInput {
          input {
            outline: none;
            border: none;
            border: 1px solid #2526297e;
            border-radius: 0.2rem;
            height: 1.5rem;
            width: 12rem;
            padding-left: 0.2rem;
            padding-right: 0.2rem;
          }
        }
      }

      .buttons-box {
        width: 100%;
        flex: 1;
        display: flex;
        align-items: flex-end;

        @media (max-width: 600px) {
          align-items: center;
          justify-content: center;
        }

        .change-password-button {
          width: auto;
          padding-left: 0.5rem;
          padding-right: 0.5rem;
          padding-bottom: 0.1rem;
          height: 2rem;
          display: flex;
          justify-content: center;
          align-items: center;
          background-color: #eeeded;
          color: #7f8081;
          border-radius: 0.2rem;
          transition: all 0.2s;

          &:hover {
            cursor: pointer;
            background-color: #e2e2e2;
          }
        }
      }

      .label {
        color: #2526297e;
        font-size: 1rem;
        width: 3.5rem;
      }

      .value {
        font-size: 1.1rem;
        color: #252629dc;
      }

      .edit {
        margin-left: 1rem;
        margin-bottom: 0.125rem;
        color: #2526297e;
        transition: 0.2s;

        &:hover {
          cursor: pointer;
          color: #252629dc;
        }
      }

      .accept {
        margin-left: 1rem;
        margin-bottom: 0.125rem;
        color: #43b64d;
        transition: 0.2s;

        &:hover {
          cursor: pointer;
          color: #39a542;
        }
      }

      .cancel {
        margin-left: 1rem;
        margin-bottom: 0.125rem;
        color: rgb(224, 116, 106);
        transition: 0.2s;

        &:hover {
          cursor: pointer;
          color: rgb(199, 90, 81);
        }
      }
    }
  }
}

.pro-box {
  width: 100%;
  height: 17rem;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  background-color: white;
  filter:drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.171));
  @media (max-width: 600px) {
    height: 50rem;
    margin-bottom: 5rem;
  }

  .label {
    width: 100%;
    height: 4rem;
    display: flex;
    align-items: center;
    padding-left: 1rem;
    font-size: 1.1rem;
    color: #252629dc;

    @media (max-width: 600px) {
      background-color: rgb(238, 237, 237);
      justify-content: center;
      height: 5rem;
      font-size: 1.2rem;
    }
  }

  .info {
    width: 100%;
    flex: auto;
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    @media (max-width: 600px) {
      flex-direction: column;
      width: 100%;
    }

    .status-box {
      width: 8rem;
      height: 8rem;
      border-radius: 0.3rem;
      background-color: white;
      border: 2px solid #2526291f;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      @media (max-width: 800px) {
        width: 7rem;
      }
      @media (max-width: 600px) {
        width: 12rem;
      }

      .title {
        width: 100%;
        height: 2.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #2526297e;
        font-size: 1rem;
        font-weight: 600;
        background-color: rgb(238, 237, 237);
      }

      .value {
        width: 100%;
        height: 5.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #2526297e;
        font-size: 1.4rem;
        font-weight: 600;
        text-transform: capitalize;

        .active {
          color: #11b6a5;
        }
      }
    }

    .period-start {
      width: 10rem;
      height: 8rem;
      border-radius: 0.3rem;
      background-color: white;
      border: 2px solid #2526291f;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      @media (max-width: 800px) {
        width: 9rem;
      }
      @media (max-width: 600px) {
        width: 12rem;
      }

      .title {
        width: 100%;
        height: 2.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #2526297e;
        font-size: 1rem;
        font-weight: 600;
        background-color: rgb(238, 237, 237);
      }

      .value {
        width: 100%;
        height: 5.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #2526297e;
        font-size: 1.2rem;
        font-weight: 600;
        text-transform: capitalize;
        padding-left: 0.5rem;
        padding-right: 0.5rem;
        text-align: center;
        line-height: 1.8rem;
      }
    }

    .period-end {
      width: 10rem;
      height: 8rem;
      border-radius: 0.3rem;
      background-color: white;
      border: 2px solid #2526291f;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      @media (max-width: 800px) {
        width: 9rem;
      }
      @media (max-width: 600px) {
        width: 12rem;
      }

      .title {
        width: 100%;
        height: 2.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #2526297e;
        font-size: 1rem;
        font-weight: 600;
        background-color: rgb(238, 237, 237);
      }

      .value {
        width: 100%;
        height: 5.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #2526297e;
        font-size: 1.2rem;
        font-weight: 600;
        text-transform: capitalize;
        padding-left: 0.5rem;
        padding-right: 0.5rem;
        text-align: center;
        line-height: 1.8rem;
      }
    }

    .recharge-box {
      width: 8rem;
      height: 8rem;
      border-radius: 0.3rem;
      background-color: white;
      border: 2px solid #2526291f;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      @media (max-width: 800px) {
        width: 7rem;
      }
      @media (max-width: 600px) {
        width: 12rem;
      }

      .title {
        width: 100%;
        height: 2.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #2526297e;
        font-size: 1rem;
        font-weight: 600;
        background-color: rgb(238, 237, 237);
        @media (max-width: 800px) {
          font-size: 0.95rem;
        }
      }

      .value {
        width: 100%;
        height: 5.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #2526297e;
        font-size: 1.2rem;
        font-weight: 600;
        text-transform: capitalize;
      }

      .on {
        color:#11b6a5;
      }

      .off {
        color:#e64f44;
      }
    }
  }

  .button-bar {
    width: 100%;
    height: 4rem;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-right: 1rem;
    @media (max-width: 600px) {
      background-color: rgb(238, 237, 237);
      height: 5rem;
      justify-content: center;
      padding-right: 0rem;
    }
  }
}

.upgrade-box {
  width: 100%;
  height: auto;
  margin-top: 3rem;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  background-color: white;
  filter:drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.171));

  .label {
    width: 100%;
    height: 4rem;
    display: flex;
    align-items: center;
    padding-left: 1rem;
    font-size: 1.1rem;
    font-weight: bold;
    color: white;
    background-color: #11b6a5;

    @media (max-width: 600px) {
      // background-color: rgb(238, 237, 237);
      justify-content: center;
      height: 5rem;
      font-size: 1.5rem;
      // font-weight: bold;
    }
  }

  .plans-box {
    width: 100%;
    height: 24rem;
    display: flex;
    align-items: center;
    justify-content: space-evenly;

    @media (max-width: 600px) {
      flex-direction: column;
      height: auto;
    }
  }

  .labels-box {

    @media (max-width: 600px) {
      display: none !important;
    }

    .title-box {
      justify-content: flex-end !important;
      @media (max-width: 800px) {
        padding-right: 0.5rem;
      }
      @media (max-width: 700px) {
        font-size: 0.9rem;
      }
    }

    .price-box {
      justify-content: flex-end !important;
      @media (max-width: 800px) {
        padding-right: 0.5rem;
      }
      @media (max-width: 700px) {
        font-size: 0.9rem;
      }
    }

    .dialogs-box {
      justify-content: flex-end !important;
      @media (max-width: 800px) {
        padding-right: 0.5rem;
      }
      @media (max-width: 700px) {
        font-size: 0.9rem;
      }
    }

    .recordings-box {
      justify-content: flex-end !important;
      @media (max-width: 800px) {
        padding-right: 0.5rem;
      }
      @media (max-width: 700px) {
        font-size: 0.9rem;
      }
    }

    .recording-time-box {
      justify-content: flex-end !important;
      @media (max-width: 800px) {
        padding-right: 0.5rem;
      }
      @media (max-width: 700px) {
        font-size: 0.9rem;
        text-align: right;
      }
    }

  }

  .basic-box {
    background-color: #11b6a5;

    .title-box {
      font-size: 1.2rem;
      font-weight: bold;
    }
  }

  .silver-box {
    background-color: #cccccc;

    .title-box {
      font-size: 1.2rem;
      font-weight: bold;
    }
  }

  .gold-box {
    background-color: #ebc659;

    .title-box {
      font-size: 1.2rem;
      font-weight: bold;
    }
  }

  .plan-box {
    width: 11rem;
    height: 20rem;
    display: flex;
    flex-direction: column;
    color: #252629;

    @media (max-width: 700px) {
      width: 8rem;
    }

    @media (max-width: 600px) {
      margin-top: 1rem;
      margin-bottom: 1rem;
      width: 90%;
    }

    .title-box {
      width: 100%;
      height: 4rem;
      display: flex;
      justify-content: center;
      align-items: center;
      border-bottom: 3px solid white;
    }

    .price-box {
      width: 100%;
      height: 3rem;
      display: flex;
      justify-content: center;
      align-items: center;
      border-bottom: 3px solid white;

      .name {
        display: none;

        @media (max-width: 600px) {
          display: flex;
          width: 50%;
          justify-content: flex-start;
          align-items: center;
          padding-left: 1rem;
        }
      }

      .value {
        @media (max-width: 600px) {
          display: flex;
          width: 50%;
          justify-content: center;
          align-items: center;
          font-weight: bold;
        }
      }
    }

    .dialogs-box {
      width: 100%;
      height: 3rem;
      display: flex;
      justify-content: center;
      align-items: center;
      border-bottom: 3px solid white;

      .name {
        display: none;

        @media (max-width: 600px) {
          display: flex;
          width: 50%;
          justify-content: flex-start;
          align-items: center;
          padding-left: 1rem;
        }
      }

      .value {
        @media (max-width: 600px) {
          display: flex;
          width: 50%;
          justify-content: center;
          align-items: center;
          font-weight: bold;
        }
      }
    }

    .recordings-box {
      width: 100%;
      height: 3rem;
      display: flex;
      justify-content: center;
      align-items: center;
      border-bottom: 3px solid white;

      .name {
        display: none;

        @media (max-width: 600px) {
          display: flex;
          width: 50%;
          justify-content: flex-start;
          align-items: center;
          padding-left: 1rem;
        }
      }

      .value {
        @media (max-width: 600px) {
          display: flex;
          width: 50%;
          justify-content: center;
          align-items: center;
          font-weight: bold;
        }
      }
    }

    .recording-time-box {
      width: 100%;
      height: 3rem;
      display: flex;
      justify-content: center;
      align-items: center;
      border-bottom: 3px solid white;

      .name {
        display: none;

        @media (max-width: 600px) {
          display: flex;
          width: 50%;
          justify-content: flex-start;
          align-items: center;
          padding-left: 1rem;
        }
      }

      .value {
        @media (max-width: 600px) {
          display: flex;
          width: 50%;
          justify-content: center;
          align-items: center;
          font-weight: bold;
        }
      }
    }

    .button-box {
      width: 100%;
      height: 4rem;
      display: flex;
      justify-content: center;
      align-items: center;
    }

  }
}

.usage-box {
  width: 100%;
  height: auto;
  margin-top: 3rem;
  margin-bottom: 0rem;
  display: flex;
  flex-direction: column;
  background-color: white;
  filter:drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.171));

  .label {
    width: 100%;
    height: 4rem;
    display: flex;
    align-items: center;
    padding-left: 1rem;
    font-size: 1.1rem;
    font-weight: bold;
    color: white;
    background-color: #11b6a5;

    @media (max-width: 600px) {
      justify-content: center;
      height: 5rem;
      font-size: 1.5rem;
    }
  }

  .content-box {
    width: 100%;
    height: 10rem;
    display: flex;
    justify-content: center;
    align-items: center;

    .center-box {
      width: 80%;
      height: auto;
      display: flex;
      flex-direction: column;

      .row {
        width: 100%;
        height: 2.5rem;
        display: flex;

        .row-label {
          flex: 1;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding-right: 1rem;
          color: #2526297e;
        }

        .value {
          flex: 4;
          height: 100%;
          display: flex;
          align-items: center;
          font-weight: bold;
          text-transform: capitalize;
          padding-left: 0.5rem;

          @media (max-width: 600px) {
            flex: 1;
          }
        }
      }
    }
  }
}
</style>